import React from 'react';
import { AlertTriangle } from 'lucide-react';
import './RiskMatrix.css';
import { cellCode, getRiskLevel, riskLabel, riskColours, RiskLevel, toFacilityBadgeKey } from '../utils/riskUtils';

interface RiskMatrixProps {
  likelihood?: number;  // 1-5
  significance?: number;  // 1-5
  onSelect?: (likelihood: number, significance: number) => void;
  readonly?: boolean;
  showLabels?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const RiskMatrix: React.FC<RiskMatrixProps> = ({
  likelihood,
  significance,
  onSelect,
  readonly = false,
  showLabels = true,
  size = 'medium'
}) => {
  const handleCellClick = (l: number, s: number) => {
    if (!readonly && onSelect) {
      onSelect(l, s);
    }
  };

  // Labels for axes
  const likelihoodLabels = [
    { value: 1, label: 'Rare', shortLabel: '1' },
    { value: 2, label: 'Unlikely', shortLabel: '2' },
    { value: 3, label: 'Possible', shortLabel: '3' },
    { value: 4, label: 'Likely', shortLabel: '4' },
    { value: 5, label: 'Almost Certain', shortLabel: '5' }
  ];

  const significanceLabels = [
    { value: 1, label: 'Insignificant', shortLabel: '1' },
    { value: 2, label: 'Minor', shortLabel: '2' },
    { value: 3, label: 'Moderate', shortLabel: '3' },
    { value: 4, label: 'Major', shortLabel: '4' },
    { value: 5, label: 'Catastrophic', shortLabel: '5' }
  ];

  return (
    <div className={`risk-matrix-container ${size}`}>
      {showLabels && (
        <div className="matrix-header">
          <div className="matrix-title">
            <AlertTriangle size={18} />
            <h4>Risk Assessment Matrix (5×5)</h4>
          </div>
          <div className="matrix-legend">
            {(['low', 'moderate', 'high', 'very_high'] as RiskLevel[]).map(level => (
              <span key={level} className={`legend-item ${toFacilityBadgeKey(level)}`}>
                <span className="legend-dot" style={riskColours[level]} aria-hidden="true" />
                {riskLabel(level)}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="risk-matrix-grid">
        {/* Y-axis label (Significance) */}
        <div className="axis-label y-axis">
          <span className="axis-title">Likelihood</span>
        </div>

        {/* Significance labels (Y-axis) */}
        <div className="y-labels">
          {[...likelihoodLabels].reverse().map(({ value, label }) => (
            <div key={value} className="axis-label-item">
              <span className="label-text">{label}</span>
              <span className="label-value">{cellCode(value, 1)[0]}</span>
            </div>
          ))}
        </div>

        {/* Matrix grid */}
        <div className="matrix-grid">
          {/* Render from high significance to low (top to bottom) */}
          {[5, 4, 3, 2, 1].map(l => (
            <div key={`row-${l}`} className="matrix-row">
              {[1, 2, 3, 4, 5].map(s => {
                const riskLevel = getRiskLevel(l, s);
                const level = riskLevel!;
                const isSelected = likelihood === l && significance === s;
                const likelihoodLabel = likelihoodLabels.find(item => item.value === l)!;
                const significanceLabel = significanceLabels.find(item => item.value === s)!;

                return (
                  <button
                    key={`${l}-${s}`}
                    type="button"
                    style={riskColours[level]}
                    className={`btn matrix-cell ${toFacilityBadgeKey(level)} ${isSelected ? 'selected' : ''} ${readonly ? 'readonly' : ''}`}
                    onClick={() => handleCellClick(l, s)}
                    disabled={readonly}
                    title={`Likelihood: ${cellCode(l, s).charAt(0)} (${likelihoodLabel.label}), Consequence: ${s} (${significanceLabel.label}), Level: ${riskLabel(level)}`}
                  >
                    <span className="cell-score">{cellCode(l, s)}</span>
                    {isSelected && <span className="selection-indicator">✓</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Likelihood labels (X-axis) */}
        <div className="x-labels-spacer"></div>
        <div className="x-labels">
          {significanceLabels.map(({ value, label }) => (
            <div key={value} className="axis-label-item">
              <span className="label-value">{value}</span>
              <span className="label-text">{label}</span>
            </div>
          ))}
        </div>

        {/* X-axis label (Likelihood) */}
        <div className="axis-label-spacer"></div>
        <div className="axis-label x-axis">
          <span className="axis-title">Consequence</span>
        </div>
      </div>

      {/* Current selection info */}
      {likelihood && significance && getRiskLevel(likelihood, significance) && (
        <div className="selection-info">
          <div className="info-row">
            <span className="info-label">Selected Risk:</span>
            <span style={riskColours[getRiskLevel(likelihood, significance)!]} className={`risk-badge ${toFacilityBadgeKey(getRiskLevel(likelihood, significance)!)}`}>
              {riskLabel(getRiskLevel(likelihood, significance)!)}
            </span>
          </div>
          <div className="info-details">
            <span>Likelihood {cellCode(likelihood, significance).charAt(0)}</span>
            <span>·</span>
            <span>Consequence {significance}</span>
            <span>·</span>
            <span>Cell {cellCode(likelihood, significance)}</span>
          </div>
        </div>
      )}

      {!readonly && !likelihood && !significance && (
        <div className="selection-prompt">
          Click a cell to select risk level
        </div>
      )}
    </div>
  );
};

export default RiskMatrix;
